import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errorMsg, nombreModelo, textoAgendaNextep, AGENDA_NEXTEP_COLOR_CLASS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { Combobox } from "@/components/Combobox";
import { DatosCuponDialog } from "@/components/DatosCuponDialog";
import { numTrabajo, fmtFecha } from "@/pages/TrabajosActivos";
import { ArrowLeft, X, MessageSquare, MessageCircle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";

const fmtMiles = (n) => (n === "" || n === null || n === undefined ? "" : Number(n).toLocaleString("es-CR"));
const APROBACIONES = [
  { value: "pendiente", label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  { value: "aprobado", label: "Aprobado", cls: "bg-teal-100 text-teal-700" },
  { value: "no_aprobado", label: "Rechazado", cls: "bg-pink-100 text-pink-700" },
];

const ESTADO_REPUESTO = {
  aprobado: { label: "Aprobado", cls: "bg-teal-100 text-teal-700" },
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  rechazado: { label: "Rechazado", cls: "bg-pink-100 text-pink-700" },
};

export default function TrabajoFinalizar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [trabajo, setTrabajo] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [kmPrueba, setKmPrueba] = useState("");
  const [notas, setNotas] = useState("");
  const [expandidas, setExpandidas] = useState(new Set());
  const [notaModal, setNotaModal] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [errorMecanico, setErrorMecanico] = useState("");
  const [waDialog, setWaDialog] = useState(null);
  const [waEnviando, setWaEnviando] = useState(false);
  const [fotosResumen, setFotosResumen] = useState({ ingreso: { total: 0, cumplidas: 0 }, salida: { total: 0, cumplidas: 0 } });
  const [cfgFotos, setCfgFotos] = useState({ fotos_exigir_ingreso_para_cerrar: false });
  const [cuponDialogOpen, setCuponDialogOpen] = useState(false);
  const [guardandoCupon, setGuardandoCupon] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, e] = await Promise.all([
          api.post(`/trabajos/${id}/sincronizar-finalizacion`).catch(() => api.get(`/trabajos/${id}`)),
          api.get("/empleados"),
        ]);
        setTrabajo(t.data);
        setKmPrueba(t.data.kilometraje_prueba ? String(t.data.kilometraje_prueba) : "");
        setNotas(t.data.notas || "");
        setEmpleados(e.data);
      } catch (err) { toast.error(errorMsg(err)); }
    })();
    api.get(`/trabajos/${id}/fotos-resumen`).then((r) => setFotosResumen(r.data)).catch(() => {});
    api.get("/config-fotos").then((r) => setCfgFotos(r.data)).catch(() => {});
  }, [id]);

  if (!trabajo) return <div className="text-slate-500 text-sm" data-testid="trabajo-cargando">Cargando...</div>;
  const soloLectura = trabajo.estado !== "activo";

  const persistir = async (cambios) => {
    try {
      const r = await api.patch(`/trabajos/${id}`, cambios);
      setTrabajo(r.data);
      return true;
    } catch (err) { toast.error(errorMsg(err)); return false; }
  };

  const tareasPayload = (lista) => lista.map(({ servicio, ...t }) => t);

  const updateTarea = (tareaId, cambios) => {
    const lista = trabajo.tareas.map((t) => (t.id === tareaId ? { ...t, ...cambios } : t));
    return persistir({ tareas: tareasPayload(lista) });
  };

  const toggleHijo = (tarea, hijoId) => {
    const set = new Set(tarea.hijos_realizados || []);
    set.has(hijoId) ? set.delete(hijoId) : set.add(hijoId);
    updateTarea(tarea.id, { hijos_realizados: [...set] });
  };

  const eliminarTarea = async (t) => {
    if (!window.confirm(`¿Eliminar la tarea "${t.servicio?.nombre}"?`)) return;
    try {
      const r = await api.delete(`/trabajos/${id}/tareas/${t.id}`);
      setTrabajo(r.data);
      toast.success("Tarea eliminada");
    } catch (err) { toast.error(errorMsg(err)); }
  };

  const guardarNota = async () => {
    if (notaModal.tipo === "tarea") await updateTarea(notaModal.id, { nota: notaModal.texto });
    else {
      const reps = trabajo.repuestos.map((r, i) => (i === notaModal.idx ? { ...r, nota: notaModal.texto } : r));
      await persistir({ repuestos: reps });
    }
    setNotaModal(null);
  };

  const fotosIngresoFaltan = cfgFotos.fotos_exigir_ingreso_para_cerrar
    && fotosResumen.ingreso.cumplidas < fotosResumen.ingreso.total;
  const MSG_FALTAN_FOTOS_INGRESO = "El asesor debe subir las fotos de ingreso para poder cerrar este trabajo";

  const cerrar = async () => {
    if (!trabajo.empleado_id) {
      setErrorMecanico("Debe asignar un mecánico antes de cerrar el trabajo");
      toast.error("Debe asignar un mecánico antes de cerrar el trabajo");
      return;
    }
    if (trabajo.es_cupon && !trabajo.orden_honda && !trabajo.orden_honda_pendiente) {
      toast.error("El asesor debe agregar el número de orden de Honda (o marcarla como pendiente) para poder cerrar este trabajo");
      api.post(`/trabajos/${id}/aviso-orden-honda`).catch(() => {});
      return;
    }
    if (fotosIngresoFaltan) {
      toast.error(MSG_FALTAN_FOTOS_INGRESO);
      return;
    }
    if (!window.confirm("¿Cerrar el trabajo? Quedará finalizado y no se podrán realizar más cambios")) return;
    setCerrando(true);
    try {
      await api.post(`/trabajos/${id}/finalizar`);
      toast.success("Trabajo finalizado de forma exitosa");
      try {
        const r = await api.get(`/trabajos/${id}/whatsapp-aviso`);
        setWaDialog(r.data);
        return;
      } catch { /* desactivado o sin permiso: continúa como antes */ }
      navigate("/trabajos/activos");
    } catch (err) { toast.error(errorMsg(err)); } finally { setCerrando(false); }
  };

  const enviarAvisoWa = async () => {
    setWaEnviando(true);
    try {
      await api.post(`/trabajos/${id}/whatsapp-aviso`, { reenviar: false });
      toast.success("Aviso puesto en cola para enviarse al cliente por WhatsApp");
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setWaEnviando(false); setWaDialog(null); navigate("/trabajos/activos"); }
  };

  const omitirAvisoWa = async (motivo) => {
    setWaDialog(null);
    try { await api.post(`/trabajos/${id}/whatsapp-aviso/omitir`, { motivo }); } catch { /* si falla, navegar igual */ }
    navigate("/trabajos/activos");
  };

  const guardarDatosCupon = async (datos) => {
    setGuardandoCupon(true);
    try {
      const r = await api.put(`/trabajos/${id}/datos-cupon`, datos);
      setTrabajo(r.data);
      toast.success("Datos del cupón actualizados");
      setCuponDialogOpen(false);
    } catch (err) { toast.error(errorMsg(err)); } finally { setGuardandoCupon(false); }
  };

  return (
    <div data-testid="trabajo-finalizar-page" className="space-y-4">
      <div className="flex items-center gap-3">
        <button data-testid="trabajo-volver-btn" onClick={() => navigate("/trabajos/activos")}
          className="text-slate-500 hover:text-slate-800"><ArrowLeft size={18} /></button>
        <h1 className="text-lg font-bold text-slate-800">
          Finalización de trabajo <span className="text-indigo-700">{numTrabajo(trabajo.numero)}</span>
        </h1>
        {soloLectura && (
          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-200 text-slate-600 uppercase">
            {trabajo.estado}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <Panel titulo="Datos del trabajo" testId="trabajo-datos-panel" className="lg:col-span-1">
          <div className="space-y-3 text-sm">
            <div><span className="text-slate-500">Cliente:</span>{" "}
              <span data-testid="trabajo-dato-cliente" className="font-medium text-slate-800">{trabajo.cliente?.nombre}</span></div>
            <div><span className="text-slate-500">Vehículo:</span>{" "}
              <span data-testid="trabajo-dato-vehiculo" className="font-medium text-slate-800">
                {nombreModelo(trabajo.vehiculo?.marca, trabajo.vehiculo?.modelo)} · {trabajo.vehiculo?.placa}</span></div>
            <div><span className="text-slate-500">Kilometraje de ingreso:</span>{" "}
              <span data-testid="trabajo-dato-km" className="font-medium text-slate-800">
                {trabajo.kilometraje !== null ? fmtMiles(trabajo.kilometraje) : "No medido"}</span></div>
            <div><span className="text-slate-500">Fecha de ingreso:</span>{" "}
              <span className="font-medium text-slate-800">{fmtFecha(trabajo.fecha_ingreso)}</span></div>
            <div className="space-y-1.5 pt-2">
              <label className="text-sm font-medium text-slate-700">Kilometraje de prueba</label>
              <Input data-testid="trabajo-km-prueba-input" inputMode="numeric" disabled={soloLectura}
                value={kmPrueba === "" ? "" : fmtMiles(kmPrueba)}
                onChange={(e) => setKmPrueba(e.target.value.replace(/\D/g, "").slice(0, 9))}
                onBlur={() => persistir({ kilometraje_prueba: kmPrueba === "" ? 0 : parseInt(kmPrueba, 10) })}
                placeholder="Ej: 12 800" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Mecánico asignado <span className="text-red-500">*</span></label>
              <Combobox testId="trabajo-mecanico-combobox" value={trabajo.empleado_id || ""}
                onChange={async (v) => { const ok = await persistir({ empleado_id: v || null }); if (ok) setErrorMecanico(""); }}
                placeholder="Seleccione Mecánico"
                options={empleados.filter((e) => e.activo).map((e) => ({
                  value: e.id, label: [e.nombre, e.primer_apellido, e.segundo_apellido].filter(Boolean).join(" ") }))} />
              {errorMecanico && <p data-testid="trabajo-mecanico-error" className="text-xs text-red-500">{errorMecanico}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Notas</label>
              <Textarea data-testid="trabajo-notas-input" value={notas} disabled={soloLectura} rows={3}
                onChange={(e) => setNotas(e.target.value)} onBlur={() => persistir({ notas })}
                placeholder="Observaciones generales del trabajo (opcional)" />
            </div>
            {textoAgendaNextep(trabajo) && (() => {
              const agendaInfo = textoAgendaNextep(trabajo);
              return (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Cita previa</label>
                  <p data-testid="trabajo-agenda-nextep-estado"
                    className={`text-sm ${AGENDA_NEXTEP_COLOR_CLASS[agendaInfo.color] || "text-slate-800"}`}>
                    {agendaInfo.texto}
                  </p>
                  {agendaInfo.detalle && <p className="text-xs text-slate-500">{agendaInfo.detalle}</p>}
                </div>
              );
            })()}
            {trabajo.es_cupon && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Orden Honda</label>
                {trabajo.orden_honda ? (
                  <p data-testid="trabajo-orden-honda-estado" className="text-sm font-semibold text-emerald-700">
                    #{trabajo.orden_honda}
                  </p>
                ) : trabajo.orden_honda_pendiente ? (
                  <p data-testid="trabajo-orden-honda-estado" className="text-sm text-amber-700">
                    Orden Honda pendiente — el asesor la marcó como pendiente
                  </p>
                ) : (
                  <p data-testid="trabajo-orden-honda-estado" className="text-sm text-red-600">
                    Falta el número de orden Honda. El asesor debe ingresarlo (o marcarlo como pendiente)
                    desde Trabajos Activos antes de poder cerrar este trabajo.
                  </p>
                )}
                <Button type="button" size="sm" variant="outline" data-testid="trabajo-datos-cupon-editar-btn"
                  onClick={() => setCuponDialogOpen(true)}
                  className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 mt-1">
                  Datos del cupón
                </Button>
              </div>
            )}
          </div>
        </Panel>

        <div className="lg:col-span-2 space-y-4">
          <Panel titulo="Tareas del trabajo" testId="trabajo-tareas-panel">
            <div className="overflow-x-auto">
              <table data-testid="trabajo-tareas-tabla" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-2.5 pr-3 font-semibold w-[32%]">Tarea</th>
                    <th className="py-2.5 pr-3 font-semibold">Aprobación</th>
                    <th className="py-2.5 pr-3 font-semibold">Realizado</th>
                    <th className="py-2.5 pr-3 font-semibold">Prioridad</th>
                    <th className="py-2.5 text-right font-semibold">Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  {trabajo.tareas.map((t) => {
                    const hijos = t.servicio?.hijos || [];
                    const abierta = expandidas.has(t.id);
                    const apr = APROBACIONES.find((a) => a.value === t.aprobacion) || APROBACIONES[1];
                    return [
                      <tr key={t.id} data-testid={`trabajo-tarea-row-${t.id}`} className="border-b border-slate-100">
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-1.5">
                            {hijos.length > 0 ? (
                              <button data-testid={`trabajo-tarea-expandir-${t.id}`}
                                onClick={() => { const n = new Set(expandidas); n.has(t.id) ? n.delete(t.id) : n.add(t.id); setExpandidas(n); }}
                                className="text-slate-500 hover:text-slate-800">
                                {abierta ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                              </button>
                            ) : <span className="w-4" />}
                            <div>
                              <span className="font-medium text-slate-800">{t.servicio?.nombre}</span>
                              {t.nueva && (
                                <span data-testid={`trabajo-tarea-nueva-badge-${t.id}`}
                                  className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-600 text-white font-bold uppercase">Nuevo</span>
                              )}
                              {t.detalle && <p className="text-xs text-slate-500">{t.detalle}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 pr-3">
                          <span data-testid={`trabajo-tarea-aprobacion-${t.id}`}
                            className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${apr.cls}`}>
                            {apr.label}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3">
                          <Switch data-testid={`trabajo-tarea-realizado-${t.id}`} checked={t.realizado}
                            disabled={soloLectura || t.aprobacion === "no_aprobado"}
                            onCheckedChange={(v) => updateTarea(t.id, { realizado: v })} />
                        </td>
                        <td className="py-2.5 pr-3">
                          <Select value={t.prioridad} disabled={soloLectura}
                            onValueChange={(v) => updateTarea(t.id, { prioridad: v })}>
                            <SelectTrigger data-testid={`trabajo-tarea-prioridad-${t.id}`} className="h-8 w-28 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="baja">Baja</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="alta">Alta</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2.5">
                          <div className="flex items-center justify-end gap-1.5">
                            <button data-testid={`trabajo-tarea-nota-btn-${t.id}`} title="Comentario"
                              onClick={() => setNotaModal({ tipo: "tarea", id: t.id, texto: t.nota || "", titulo: t.servicio?.nombre })}
                              className={`w-7 h-7 flex items-center justify-center rounded-full ${t.nota ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-500"} hover:bg-teal-100`}>
                              <MessageSquare size={13} />
                            </button>
                            {t.nueva && !soloLectura && (
                              <button data-testid={`trabajo-tarea-eliminar-btn-${t.id}`} title="Eliminar"
                                onClick={() => eliminarTarea(t)}
                                className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>,
                      ...(hijos.length > 0 && abierta ? [
                        <tr key={`${t.id}-hijos`} className="border-b border-slate-100 bg-slate-50/60">
                          <td colSpan={5} className="py-2 px-6">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Checklist</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                              {hijos.map((h) => (
                                <label key={h.id} data-testid={`trabajo-hijo-item-${t.id}-${h.id}`}
                                  className="flex items-center gap-2 text-sm text-slate-700">
                                  <Switch data-testid={`trabajo-hijo-realizado-${t.id}-${h.id}`}
                                    checked={(t.hijos_realizados || []).includes(h.id)} disabled={soloLectura}
                                    onCheckedChange={() => toggleHijo(t, h.id)} className="scale-90" />
                                  {h.nombre}
                                  <span className="text-xs text-slate-400">¿Realizado?</span>
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>,
                      ] : []),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel titulo="Indique los repuestos cambiados" testId="trabajo-repuestos-panel">
            {(trabajo.repuestos || []).length === 0 ? (
              <p data-testid="trabajo-repuestos-vacio" className="text-sm text-slate-400 py-2">
                Este trabajo aún no tiene repuestos asociados. Se cargarán desde la cotización del trabajo.
              </p>
            ) : (
              <div className="space-y-2">
                {trabajo.repuestos.map((r, i) => (
                  <div key={i} data-testid={`trabajo-repuesto-item-${i}`}
                    className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <label className={`flex items-center gap-2 flex-1 ${!r.aprobado ? "opacity-70" : ""}`}>
                      <Checkbox data-testid={`trabajo-repuesto-instalado-${i}`} checked={!!r.instalado}
                        disabled={soloLectura || !r.aprobado}
                        onCheckedChange={(v) => persistir({
                          repuestos: trabajo.repuestos.map((x, j) => (j === i ? { ...x, instalado: !!v } : x)) })} />
                      <span className="text-slate-800">{r.nombre || r.codigo_parte || r.repuesto_id}</span>
                      {r.cantidad ? <span className="text-xs text-slate-500">x{r.cantidad}</span> : null}
                      <span className="text-xs text-slate-400">Instalado</span>
                      {(() => {
                        const es = ESTADO_REPUESTO[r.estado_solicitud] ||
                          (r.aprobado ? ESTADO_REPUESTO.aprobado : { label: "Sin aprobar", cls: "bg-amber-100 text-amber-800" });
                        return (
                          <span data-testid={`trabajo-repuesto-aprobado-badge-${i}`}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${es.cls}`}>
                            {es.label}
                          </span>
                        );
                      })()}
                      {r.aportado_cliente && (
                        <span data-testid={`trabajo-repuesto-aportado-cliente-${i}`}
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase bg-amber-100 text-amber-800">
                          Aportado por cliente
                        </span>
                      )}
                    </label>
                    <button data-testid={`trabajo-repuesto-nota-btn-${i}`} title="Comentario"
                      onClick={() => setNotaModal({ tipo: "repuesto", idx: i, texto: r.nota || "", titulo: r.nombre || "Repuesto" })}
                      className={`w-7 h-7 flex items-center justify-center rounded-full ${r.nota ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-500"} hover:bg-teal-100`}>
                      <MessageSquare size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel titulo="Fotos del trabajo" testId="trabajo-fotos-resumen-panel">
            <p data-testid="trabajo-fotos-resumen-aviso" className="text-sm text-slate-600">
              Fotos de ingreso: {fotosResumen.ingreso.cumplidas} de {fotosResumen.ingreso.total} · Fotos de
              salida: {fotosResumen.salida.cumplidas} de {fotosResumen.salida.total} (las sube el asesor)
            </p>
          </Panel>

          {!soloLectura && tienePermiso("trabajos.finalizar") && (
            <div className="flex flex-col items-end gap-1.5">
              {trabajo.es_cupon && !trabajo.orden_honda && !trabajo.orden_honda_pendiente && (
                <p data-testid="trabajo-cerrar-bloqueado-aviso" className="text-xs text-red-500 text-right max-w-sm">
                  El asesor debe agregar el número de orden de Honda (o marcarla como pendiente) para poder cerrar este trabajo
                </p>
              )}
              {fotosIngresoFaltan && (
                <p data-testid="trabajo-cerrar-fotos-bloqueado-aviso" className="text-xs text-red-500 text-right max-w-sm">
                  {MSG_FALTAN_FOTOS_INGRESO}
                </p>
              )}
              <Button data-testid="trabajo-cerrar-btn" onClick={cerrar} disabled={cerrando || fotosIngresoFaltan}
                className={`px-8 text-white ${
                  (trabajo.es_cupon && !trabajo.orden_honda && !trabajo.orden_honda_pendiente) || fotosIngresoFaltan
                    ? "bg-teal-600/40 hover:bg-teal-600/40 cursor-not-allowed"
                    : "bg-teal-600 hover:bg-teal-700"}`}>
                <CheckCircle2 size={16} className="mr-1.5" />
                {cerrando ? "Cerrando..." : "Cerrar Trabajo"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!notaModal} onOpenChange={(o) => { if (!o) setNotaModal(null); }}>
        <DialogContent data-testid="trabajo-nota-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Comentario</DialogTitle>
            <DialogDescription>{notaModal?.titulo}</DialogDescription>
          </DialogHeader>
          {notaModal && (
            <>
              <Textarea data-testid="trabajo-nota-textarea" value={notaModal.texto} rows={4}
                disabled={soloLectura}
                onChange={(e) => setNotaModal({ ...notaModal, texto: e.target.value })}
                placeholder="Escriba la observación..." />
              <div className="flex justify-end gap-2">
                <Button variant="outline" data-testid="trabajo-nota-cancelar" onClick={() => setNotaModal(null)}>Cancelar</Button>
                {!soloLectura && (
                  <Button data-testid="trabajo-nota-guardar" onClick={guardarNota}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white">Guardar</Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!waDialog}>
        <DialogContent data-testid="whatsapp-aviso-dialog" className="max-w-md" hideClose
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle size={18} className="text-green-600" />
              ¿Desea avisar al cliente por WhatsApp?
            </DialogTitle>
            <DialogDescription>El trabajo ya quedó finalizado.</DialogDescription>
          </DialogHeader>
          {waDialog?.telefono_valido ? (
            <>
              <p className="text-xs text-slate-500">Se enviará al número <b>{waDialog.telefono}</b>:</p>
              <p data-testid="whatsapp-aviso-preview" className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 text-slate-700">
                {waDialog.texto_preview}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" data-testid="whatsapp-aviso-no-enviar" onClick={() => omitirAvisoWa("no_enviar")}>No enviar</Button>
                <Button data-testid="whatsapp-aviso-enviar" onClick={enviarAvisoWa} disabled={waEnviando}
                  className="bg-green-600 hover:bg-green-700 text-white">
                  {waEnviando ? "Enviando..." : "Enviar aviso"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p data-testid="whatsapp-aviso-sin-telefono" className="text-sm text-amber-700">
                El cliente no tiene un teléfono válido registrado; no se puede enviar el aviso por WhatsApp.
              </p>
              <div className="flex justify-end">
                <Button data-testid="whatsapp-aviso-cerrar" onClick={() => omitirAvisoWa("sin_telefono")}>Entendido</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DatosCuponDialog open={cuponDialogOpen} onOpenChange={setCuponDialogOpen}
        valorInicial={trabajo.datos_cupon} vehiculo={trabajo.vehiculo} cuponIncompleto={false}
        onGuardar={guardarDatosCupon} guardando={guardandoCupon} />
    </div>
  );
}
